import { formatCents, formatCount, tt } from '@agos/shared'

/**
 * What a franchise consumed from the platform's third-party services, and what it came to.
 *
 * **`revenueCents` arrives as a string while `costCents` beside it is a number.** Measured:
 * `"costCents": 0, "revenueCents": "0"` on the same object. Both are parsed rather than used, and
 * neither goes through `parseMoneyToCents` — they are **already cents**, and that helper reads euros, so
 * it would multiply them by a hundred.
 *
 * **`revenueCents` on the `tatrapay` row is not the platform's revenue.** It is 837 305 — 8 373,05 € —
 * against a CardPay margin of 32,02 € for the same month. It is the **volume that passed through**, and
 * legacy's tile says so (*"Prijaté cez CardPay"*) while its table column says only *"Skutočné EUR"*.
 * The two screens also count different things: 406 API **calls** here against 386 settled **payments**
 * there, over windows 40 minutes apart. Neither is wrong; the port names both so nobody has to work out
 * why 8 373 ≠ 7 999.
 *
 * **The per-unit price is fractional.** `api-pricing` gives `priceCentsPerUnit: "5.0000"` for Resend —
 * cents, with four decimals, because you bill ten thousand units. Multiply first and round once, never
 * the other way round.
 */

export interface ServiceUsage {
  service: string
  calls: number
  success: number
  failure: number
  units: number
  costCents: number
  /** Cents. For `tatrapay` this is the volume processed, not what the platform earned. */
  amountCents: number
}

/** One day's calls for one service - the `daily` series the screen used to drop. */
export interface DailyUsage {
  day: string
  service: string
  calls: number
  success: number
  failure: number
  units: number
}

/** One operation within a service, which is the only place the screen says *what* was called. */
export interface OperationUsage {
  service: string
  operation: string
  calls: number
  success: number
  failure: number
  units: number
  amountCents: number
}

export interface UsageReport {
  franchiseName: string
  from: string
  to: string
  totals: ServiceUsage[]
  daily: DailyUsage[]
  byOperation: OperationUsage[]
}

/**
 * What each service is called, rather than its key.
 *
 * Ported from legacy's `SERVICE_LABELS`. The screen showed `claude_translate` and `google_gcs` as
 * they come off the wire, which is the single biggest reason it read as unfinished - the operator
 * is being shown a column name.
 *
 * **An unknown key falls back to itself** rather than to a dash: a service added to the backend
 * tomorrow must still be countable here, and a row labelled `some_new_service` is informative
 * where an empty cell is not.
 *
 * **Twelve of the thirteen are product names and need no translation.** The thirteenth,
 * `claude_translate`, is Slovak, and §8/§14.2 wants user-visible Slovak to go through `<T>`/`tt()` -
 * which it does: `FranchiseUsagePage` renders every label as `<T>{serviceLabel(...)}</T>`, exactly
 * as `agos-nav.ts` does with its sixty-two. These are *source strings for the translator*, so that
 * one line carries a single-line exemption. **Keep the directive on one line**: a multi-line reason
 * makes `disable-next-line` apply to the comment's own second line, which is how the first attempt
 * at this reported an unused directive and left the error in place.
 */
const SERVICE_LABELS: Record<string, string> = {
  resend: 'Resend (e-mail)',
  bulkgate: 'BulkGate (SMS)',
  google_maps: 'Google Maps',
  google_gcs: 'Google Cloud Storage',
  tatrapay: 'Tatrapay+',
  fiskalpro: 'FiskalPRO',
  hikvision: 'Hikvision',
  web_push: 'Web Push',
  openai: 'OpenAI',
  // eslint-disable-next-line no-restricted-syntax -- a translator source string; see above
  claude_translate: 'AI prekladač (Claude)',
  cloudflare: 'Cloudflare',
}

export function serviceLabel(service: string): string {
  return SERVICE_LABELS[service] ?? service
}

/**
 * A bar segment's colour per service - `SAAS.md §3.111`, ported from legacy's `SERVICE_COLORS`.
 *
 * **Only the background token.** Legacy's map holds three classes per service (background, text,
 * border) and its own daily chart takes the first with `.split(" ")[0]`, because a segment is a block
 * of colour with no text in it. Ported the same way, as one token per key, so nothing has to be split
 * at render time.
 *
 * **An unknown service gets a neutral tint rather than nothing.** A service the backend starts metering
 * tomorrow must still be visible in the chart; an unstyled segment is an invisible one, and the day's
 * total would then not account for its own height.
 */
const SERVICE_TINTS: Record<string, string> = {
  resend: 'bg-amber-500/60',
  bulkgate: 'bg-blue-500/60',
  google_maps: 'bg-emerald-500/60',
  google_gcs: 'bg-teal-500/60',
  tatrapay: 'bg-violet-500/60',
  fiskalpro: 'bg-orange-500/60',
  hikvision: 'bg-cyan-500/60',
  web_push: 'bg-pink-500/60',
  openai: 'bg-fuchsia-500/60',
  claude_translate: 'bg-indigo-500/60',
  cloudflare: 'bg-rose-500/60',
}

export function serviceTint(service: string): string {
  return SERVICE_TINTS[service] ?? 'bg-muted-foreground/60'
}

/** Cents that may arrive as a number or as a decimal string. Never euros. */
function centsOf(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value) : 0
  if (typeof value !== 'string' || value.trim() === '') return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? Math.round(parsed) : 0
}

function count(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export function usageOf(payload: unknown): UsageReport {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const franchise = (typeof row.franchise === 'object' && row.franchise !== null ? row.franchise : {}) as Record<string, unknown>
  const window = (typeof row.window === 'object' && row.window !== null ? row.window : {}) as Record<string, unknown>
  const totals = Array.isArray(row.totals) ? row.totals : []

  return {
    franchiseName: typeof franchise.name === 'string' ? franchise.name : '',
    from: typeof window.from === 'string' ? window.from : '',
    to: typeof window.to === 'string' ? window.to : '',
    // Busiest first: 26 203 Google Maps calls against 185 translations is the shape of the month.
    totals: totals
      .map((entry) => {
        const service = (typeof entry === 'object' && entry !== null ? entry : {}) as Record<string, unknown>
        return {
          service: typeof service.service === 'string' ? service.service : '',
          calls: count(service.calls),
          success: count(service.success),
          failure: count(service.failure),
          units: count(service.units),
          costCents: centsOf(service.costCents),
          amountCents: centsOf(service.revenueCents),
        }
      })
      .sort((left, right) => right.calls - left.calls),
    // Chronological, because this is a series and a reader follows it left to right. The server
    // already orders it, and re-sorting here is what makes the page independent of that.
    daily: (Array.isArray(row.daily) ? row.daily : [])
      .map((entry) => {
        const day = (typeof entry === 'object' && entry !== null ? entry : {}) as Record<string, unknown>
        return {
          day: typeof day.day === 'string' ? day.day : '',
          service: typeof day.service === 'string' ? day.service : '',
          calls: count(day.calls),
          success: count(day.success),
          failure: count(day.failure),
          units: count(day.units),
        }
      })
      .filter((entry) => entry.day !== '')
      .sort((left, right) => left.day.localeCompare(right.day)),
    byOperation: (Array.isArray(row.byOperation) ? row.byOperation : [])
      .map((entry) => {
        const operation = (typeof entry === 'object' && entry !== null ? entry : {}) as Record<string, unknown>
        return {
          service: typeof operation.service === 'string' ? operation.service : '',
          operation: typeof operation.operation === 'string' ? operation.operation : '',
          calls: count(operation.calls),
          success: count(operation.success),
          failure: count(operation.failure),
          units: count(operation.units),
          amountCents: centsOf(operation.revenueCents),
        }
      })
      .sort((left, right) => right.calls - left.calls),
  }
}

/** One bar per day, summed across services - what the series is actually read for. */
export interface DayTotal {
  day: string
  calls: number
  failure: number
}

/**
 * The daily series collapsed to one row per day, in order.
 *
 * The server groups by `(day, service)`, so a day with four services is four rows and a bare list
 * of them is not a series anybody can read.
 */
export function dailyTotals(report: UsageReport): DayTotal[] {
  const byDay = new Map<string, DayTotal>()
  for (const entry of report.daily) {
    const existing = byDay.get(entry.day) ?? { day: entry.day, calls: 0, failure: 0 }
    existing.calls += entry.calls
    existing.failure += entry.failure
    byDay.set(entry.day, existing)
  }
  return [...byDay.values()].sort((left, right) => left.day.localeCompare(right.day))
}

/** One service's slice of one day's bar. */
export interface DaySegment {
  service: string
  calls: number
  /** Share of **that day's** total, so the segments of a bar always sum to 100. */
  percent: number
}

/**
 * A day's bar, split by service - §3.111.
 *
 * **The question this screen exists to answer.** A single-colour bar says *the 14th was busy*; legacy's
 * stacked one says *the 14th was busy with Google Maps*, which is the difference between a chart and a
 * number. Measured on the owner's own month, one service accounts for 26 203 of the calls and the next
 * for 185 - so an unsplit bar is a chart of one service with the rest invisible.
 *
 * **Shares of the day, not of the peak.** `dayBarPercent` already scales the bar's *height* against the
 * busiest day; inside it the segments have to close to 100 or the bar draws short and reads as a quieter
 * day than it was.
 *
 * Busiest service first, so the colours stack in a stable order across days rather than moving with
 * whatever the server happened to list first.
 */
export function daySegments(report: UsageReport, day: string): DaySegment[] {
  const rows = report.daily.filter((entry) => entry.day === day && entry.calls > 0)
  const total = rows.reduce((sum, entry) => sum + entry.calls, 0)
  if (total <= 0) return []
  return rows
    .map((entry) => ({
      service: entry.service,
      calls: entry.calls,
      percent: (entry.calls / total) * 100,
    }))
    .sort((left, right) => right.calls - left.calls || left.service.localeCompare(right.service))
}

/**
 * Every service that appears in the daily rows, busiest first - the chart's legend.
 *
 * **Read off `daily` rather than off `totals`**, which are two different populations: `totals` covers
 * the window and `daily` only the days the server returned rows for. A legend naming a colour the chart
 * does not contain is worse than a short legend, because it invites somebody to look for it.
 */
export function servicesInDaily(report: UsageReport): string[] {
  const calls = new Map<string, number>()
  for (const entry of report.daily) {
    if (entry.calls <= 0) continue
    calls.set(entry.service, (calls.get(entry.service) ?? 0) + entry.calls)
  }
  return [...calls.entries()]
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .map(([service]) => service)
}

/**
 * Units across every service - legacy's second KPI tile, *Jednotiek (SMS, e-maily, atď.)*.
 *
 * **Not the same question as calls**, which is why legacy draws both: one Resend call sends one e-mail
 * and one BulkGate call may send several SMS, so units is what the platform is billed on and calls is
 * what the code did.
 */
export function totalUnits(report: UsageReport): number {
  return report.totals.reduce((sum, usage) => sum + usage.units, 0)
}

/**
 * How many customer payments actually went through - legacy's fourth KPI, the count in its label.
 *
 * `costSummary` already reports the euros that passed through. **The count is the other half and it
 * is not derivable from the money**: 8 373,05 € is 200 payments or 2, and "how many card payments did
 * this franchise take" is the question an operator asks before "how much".
 */
export function passThroughCount(report: UsageReport): number {
  return report.totals
    .filter((usage) => isPassThrough(usage))
    .reduce((sum, usage) => sum + usage.success, 0)
}

/** The busiest day's call count, which is what every bar is drawn as a share of. */
export function peakDayCalls(report: UsageReport): number {
  return dailyTotals(report).reduce((peak, day) => Math.max(peak, day.calls), 0)
}

/**
 * A bar's share of the peak, as a percentage, floored at 2 so a non-zero day is still visible.
 *
 * A day with 3 calls against a peak of 26 203 is 0,01 % and would draw as nothing at all - which
 * reads as "no calls that day" and is wrong.
 */
export function dayBarPercent(report: UsageReport, day: DayTotal): number {
  const peak = peakDayCalls(report)
  if (peak <= 0 || day.calls <= 0) return 0
  return Math.max(2, Math.round((day.calls / peak) * 100))
}

/**
 * `19. 9.` - a day label short enough to sit under a bar.
 *
 * **Counting the parts is not enough**, which a test caught: `not-a-day` splits into three too, and
 * the first version of this answered `NaN. NaN.`. The parts have to be numbers.
 */
export function dayLabel(day: string): string {
  const parts = day.split('-')
  if (parts.length !== 3) return day
  const month = Number(parts[1])
  const date = Number(parts[2])
  if (!Number.isFinite(month) || !Number.isFinite(date) || parts[1] === '' || parts[2] === '') {
    return day
  }
  return `${date}. ${month}.`
}

/** The operations of one service, busiest first. */
export function operationsOf(report: UsageReport, service: string): OperationUsage[] {
  return report.byOperation.filter((entry) => entry.service === service)
}

/** `1. 8. 2026 - 19. 9. 2026`, or empty when the server sent no window. */
export function windowLabel(report: UsageReport): string {
  const from = dateOnly(report.from)
  const to = dateOnly(report.to)
  if (from === '' || to === '') return ''
  return `${from} - ${to}`
}

function dateOnly(timestamp: string): string {
  const day = timestamp.slice(0, 10).split('-')
  if (day.length !== 3) return ''
  const month = Number(day[1])
  const date = Number(day[2])
  // The same trap `dayLabel` fell into: `2026-ab-cd` splits into three and would read
  // `NaN. NaN. 2026`. Hardened here too rather than left because the server happens to send ISO.
  if (!Number.isFinite(month) || !Number.isFinite(date) || day[1] === '' || day[2] === '') {
    return ''
  }
  return `${date}. ${month}. ${day[0]}`
}

/** Services whose `revenueCents` is money that passed through rather than money the platform made. */
const PASS_THROUGH = new Set(['tatrapay'])

export function isPassThrough(usage: ServiceUsage): boolean {
  return PASS_THROUGH.has(usage.service)
}

/**
 * What the money column means for this row, said rather than left to the header.
 *
 * The one label cannot cover both: on `tatrapay` it is 8 373,05 € of customers' payments, and on
 * `claude_translate` it is what the platform was charged.
 */
export function amountLabel(usage: ServiceUsage): string {
  if (usage.amountCents === 0 && usage.costCents === 0) return ''
  return isPassThrough(usage) ? tt('Prijaté od zákazníkov') : tt('Náklad platformy')
}

export function amountCents(usage: ServiceUsage): number {
  return isPassThrough(usage) ? usage.amountCents : usage.costCents
}

/** `0,9 %` — and whether it is worth looking at. */
export function failureRate(usage: ServiceUsage): number {
  return usage.calls > 0 ? (usage.failure / usage.calls) * 100 : 0
}

/** Legacy's own threshold, kept: above 5 % the rate is drawn as a problem. */
export const FAILURE_ALARM_PCT = 5

export function isFailing(usage: ServiceUsage): boolean {
  return failureRate(usage) > FAILURE_ALARM_PCT
}

export function failureNote(usage: ServiceUsage): string {
  if (usage.failure === 0) return ''
  return `${formatCount(usage.failure)} ${tt('z')} ${formatCount(usage.calls)}`
}

/** The totals across every service, for the figure at the top. */
export function callTotals(report: UsageReport): { calls: number; failures: number; ratePct: number } {
  const calls = report.totals.reduce((sum, usage) => sum + usage.calls, 0)
  const failures = report.totals.reduce((sum, usage) => sum + usage.failure, 0)
  return { calls, failures, ratePct: calls > 0 ? (failures / calls) * 100 : 0 }
}

/**
 * What the platform was actually charged, which excludes anything passing through.
 *
 * Summing the money column as it stands would report 8 373,05 € of cost for a month that cost 0,17 €.
 */
export function platformCostCents(report: UsageReport): number {
  return report.totals.reduce((sum, usage) => sum + usage.costCents, 0)
}

export function passThroughCents(report: UsageReport): number {
  return report.totals
    .filter(isPassThrough)
    .reduce((sum, usage) => sum + usage.amountCents, 0)
}

export function costSummary(report: UsageReport): string {
  return `${formatCents(platformCostCents(report))} ${tt('nákladov')} · ${formatCents(passThroughCents(report))} ${tt('prešlo cez platby')}`
}
